import React, { createContext, useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [loading, setLoading] = useState(true); // Tambahkan loading state
    const idleTimer = useRef(null);
    const refreshTimer = useRef(null);

    const IDLE_TIMEOUT = 60 * 60000;

    const resetIdleTimer = () => {
        if (idleTimer.current) {
            clearTimeout(idleTimer.current);
        }
        idleTimer.current = setTimeout(() => {
            alert('You have been logged out due to inactivity.');
            logout();
        }, IDLE_TIMEOUT);
    };

    const monitorUserActivity = () => {
        window.addEventListener('mousemove', resetIdleTimer);
        window.addEventListener('keydown', resetIdleTimer);
        window.addEventListener('click', resetIdleTimer);
        resetIdleTimer();
    };

    const checkTokenExpiration = (expTime) => {
        const timeUntilExpiration = expTime * 1000 - Date.now();
        if (timeUntilExpiration > 60000) {
            refreshTimer.current = setTimeout(() => {
                refreshToken();
            }, timeUntilExpiration - 60000);
        } else {
            refreshToken();
        }
    };

    const refreshToken = async () => {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            logout();
            return;
        }

        try {
            setIsRefreshing(true);
            const response = await api.post('/auth/refreshToken', { token: refreshToken });
            const { token } = response.data;
            const decodedToken = jwtDecode(token);

            localStorage.setItem('token', token);
            setUser({ username: localStorage.getItem('username'), role: localStorage.getItem('role') });
            checkTokenExpiration(decodedToken.exp);
            setIsRefreshing(false);
            setLoading(false); // Hentikan loading setelah token divalidasi
        } catch (error) {
            console.error("Failed to refresh token:", error);
            logout();
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        const role = localStorage.getItem('role');
        const storedPermissions = JSON.parse(localStorage.getItem('permissions'));
        const username = localStorage.getItem('username');

        if (token && role && storedPermissions) {
            const decodedToken = jwtDecode(token);
            if (decodedToken.exp * 1000 < Date.now()) {
                refreshToken();
            } else {
                // Token valid, atur data user dan hentikan loading
                setUser({ username, role });
                setPermissions(storedPermissions);
                checkTokenExpiration(decodedToken.exp);
                setLoading(false);
            }
        } else {
            setLoading(false); // Token tidak valid, hentikan loading
            logout(); // Jika token tidak valid, logout pengguna
        }

        monitorUserActivity();

        return () => {
            window.removeEventListener('mousemove', resetIdleTimer);
            window.removeEventListener('keydown', resetIdleTimer);
            window.removeEventListener('click', resetIdleTimer);
            clearTimeout(idleTimer.current);
            clearTimeout(refreshTimer.current);
        };
    }, []);

    const login = async (username, password) => {
        try {
            const response = await api.post('/auth/login', { username, password });
            const { token, refreshToken, role, permissions } = response.data;
            const decodedToken = jwtDecode(token);

            localStorage.setItem('token', token);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('role', role);
            localStorage.setItem('username', username);
            localStorage.setItem('permissions', JSON.stringify(permissions));

            setUser({ username, role });
            setPermissions(permissions);
            checkTokenExpiration(decodedToken.exp);

            return true;
        } catch (error) {
            console.error("Login failed: ", error);
            return false;
        }
    };

    const logout = () => {
        setUser(null);
        setPermissions([]);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('role');
        localStorage.removeItem('permissions');
        if (idleTimer.current) clearTimeout(idleTimer.current);
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        navigate('/login');
    };

    if (loading) {
        return <div>Loading...</div>; // Tampilkan loading saat token divalidasi
    }

    return (
        <AuthContext.Provider value={{ user, login, logout, permissions, isRefreshing }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
